import { z } from 'zod'
import { streamColumnLabel } from './stream-columns'
import { hexColorSchema } from './template-primitives'
import {
  ECHO_SECTION_KEY,
  GENERIC_SECTION_KEYS,
  inferSectionDisplay,
  type SectionArticle,
  type SectionDisplay,
} from './stream-sections'

/**
 * A build section as a dashboard module (CLI-758 follow-up): what it shows and how it is drawn,
 * kept on the block — the recipe, never the data, so it redraws from whatever the build computed
 * last. `view: 'auto'` is the default picked from the section's shape (`autoSectionView`); the
 * module's own menu picks another. The other settings are the recipe's too, with defaults that
 * draw a section well; the menu does not offer them yet.
 */

export const SECTION_VIEWS = [
  'auto',
  /** Headline figures as a strip of tiles. */
  'kpis',
  /** Slim ranked bars, one figure. */
  'bars',
  /** A compact table: name, its headline, two figures. */
  'table',
  /** Each row as its share of the whole. */
  'donut',
  /** Rows that each tell a story (a narrative, a competitor, a place): one at a time, picked from
   *  a strip of names, with its summary, its momentum and its breakdowns. */
  'stories',
  /** Records of text, as small cards. */
  'cards',
  /** An echo chain's hops. */
  'chains',
  /** Counts over days, as columns. */
  'series',
] as const
export type SectionViewKind = (typeof SECTION_VIEWS)[number]

/**
 * The colours a module may be drawn in, by name — the brand system's, so "red" is the product's red
 * in light and dark mode — or a client's own `#rrggbb`, the one literal accepted.
 */
const SECTION_COLORS = [
  'blue',
  'green',
  'orange',
  'teal',
  'purple',
  'red',
  'amber',
  'gray',
  'navy',
] as const
const sectionColorSchema = z.union([z.enum(SECTION_COLORS), hexColorSchema])

export const sectionViewSchema = z.object({
  /** The section by the build's key, or a part of it by path: `narratives`, `whitespace.outlets`. */
  key: z.string().min(1).max(120),
  view: z.enum(SECTION_VIEWS).default('auto'),
  /** Rows shown before "Show all". */
  limit: z.number().int().min(1).max(50).default(8),
  /** The figure the bars draw and a value sort orders by; null = the section's most telling. */
  value: z.string().max(80).nullable().default(null),
  /** `build` keeps the build's own order (it ranks its lists); `value` biggest first; `label` A–Z. */
  sort: z.enum(['build', 'value', 'label']).default('build'),
  /** Show each row's written summary (otherwise one click away). */
  summaries: z.boolean().default(false),
  /** Show the model-written headline under a row's name. */
  subtitles: z.boolean().default(true),
  /** The bars' colour; null = the brand's. */
  color: sectionColorSchema.nullable().default(null),
})
export type SectionView = z.infer<typeof sectionViewSchema>

/** The section (or part) a view names, or undefined when the build has no such thing. */
export function sectionAt(sections: Record<string, unknown>, key: string): unknown {
  let value: unknown = sections
  for (const part of key.split('.')) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined
    value = (value as Record<string, unknown>)[part]
  }
  return value
}

/** The views a display can be drawn as — what the module's menu offers. Only a ranking has more
 *  than one: every other shape has the one view that draws it. */
export function sectionViewsFor(display: SectionDisplay): SectionViewKind[] {
  switch (display.kind) {
    case 'ranking':
      return tellsStories(display)
        ? ['stories', 'table', 'bars', 'donut', 'cards']
        : ['bars', 'table', 'donut', 'cards']
    case 'cards':
      return ['cards']
    case 'facts':
      return ['kpis']
    case 'chains':
      return ['chains']
    case 'series':
      return ['series']
    default:
      return ['auto']
  }
}

/** A figure that is the client's part of each row: `share`, `ttd_share`, `brand_share`. */
const SHARE_KEY = /(^|_)share$/i
const GROWTH_KEY = /growth/i

/** The client's-share figure among a ranking's, if it has one — drawn as the solid part of each
 *  row's bar, the rest pale (a whitespace view's whole point). */
export const sectionShareKey = (keys: readonly string[]) => keys.find((key) => SHARE_KEY.test(key))

/** A party a build names in a key (`ttd`, `brand`): a short one is an acronym, the rest a word. */
function ownerLabel(owner: string): string {
  return owner.length <= 4
    ? owner.toUpperCase()
    : owner.charAt(0).toUpperCase() + owner.slice(1).replace(/_/g, ' ')
}

/** Whose share a `*_share` is, for a legend: `ttd_share` → "TTD", `brand_share` → "Brand". */
export function sectionShareOwner(key: string): string {
  const owner = key.replace(SHARE_KEY, '')
  return owner ? ownerLabel(owner) : 'Share'
}

/** The growth figure among a ranking's, if it has one — drawn as a signed pill. */
export const sectionGrowthKey = (keys: readonly string[]) =>
  keys.find((key) => GROWTH_KEY.test(key))

/** Past this many rows, a strip of names to pick from is a wall: a table reads better. */
const STORIES_MAX = 15

/** Whether a ranking's rows tell stories: most of them carry a written summary. */
function tellsStories(display: Extract<SectionDisplay, { kind: 'ranking' }>): boolean {
  const told = display.rows.filter((row) => row.summary).length
  return told > 0 && told >= display.rows.length / 2
}

/**
 * The default view for a display, judged from its shape: headline figures as tiles, share-like
 * rows as slim bars, an entity named by what it is with a model-written headline as a compact
 * table, records of text as cards. A group (a section with parts) draws each part its own way.
 */
export function autoSectionView(display: SectionDisplay): Exclude<SectionViewKind, 'auto'> | null {
  switch (display.kind) {
    case 'facts':
      return 'kpis'
    case 'ranking':
      // A few rows that each carry a written summary read one story at a time; a row with a
      // headline of its own reads as a table line; bare figures as bars.
      // A client's share of each row is the point where there is one: split bars show it.
      if (sectionShareKey(display.valueKeys.slice(1))) return 'bars'
      if (tellsStories(display) && display.rows.length <= STORIES_MAX) return 'stories'
      return display.rows.some((row) => row.subtitle) ? 'table' : 'bars'
    case 'cards':
      return 'cards'
    case 'chains':
      return 'chains'
    case 'series':
      return 'series'
    default:
      return null
  }
}

/** The id of a section's module in the default rows — one rule wherever a module is named. */
export const sectionModuleId = (key: string) => `section-${key}`.slice(0, 100)

/**
 * A section's title on a module card, from its key: `whitespace.outlets` → "Whitespace · Outlets",
 * a coverage twin as its base's title over that party's coverage. The echo chains every build
 * writes are the one section named for what they are, Echoes.
 */
export function sectionTitle(key: string): string {
  const [head, ...rest] = key.split('.')
  const twin = coverageTwin(head!)
  const base =
    head === ECHO_SECTION_KEY
      ? 'Echoes'
      : twin
        ? `${streamColumnLabel(twin.base)} — ${twin.owner} coverage`
        : streamColumnLabel(head!)
  return rest.length > 0 ? `${base} · ${rest.map(streamColumnLabel).join(' · ')}` : base
}

/** The sections a build wrote that make modules of their own: everything but the filter lists and
 *  the echo section (its chains are what the Echoes module draws), and nothing empty. */
export function moduleSectionKeys(sections: Record<string, unknown>): string[] {
  return Object.entries(sections)
    .filter(
      ([key]) =>
        key !== ECHO_SECTION_KEY && !(GENERIC_SECTION_KEYS as readonly string[]).includes(key),
    )
    .filter(([key]) => {
      // A coverage twin is its base's toggle, not a module of its own.
      const twin = coverageTwin(key)
      return !twin || !(twin.base in sections)
    })
    .filter(([, value]) => showsSomething(inferSectionDisplay(value)))
    .map(([key]) => key)
}

/** Whether a section has anything to show: a group whose every list came back empty and that
 *  keeps only how it would have been computed (every list empty, a growth definition beside them)
 *  shows nothing, and makes no module. A group's own figures are something to show. */
function showsSomething(display: SectionDisplay): boolean {
  if (display.kind === 'empty') return false
  if (display.kind !== 'group') return true
  return display.children.some((child) =>
    child.display.kind === 'facts' ? !isDefinitionKey(child.key) : showsSomething(child.display),
  )
}

/** A part that says how the build computed its siblings (`growth_definition`): a caption, not
 *  figures to read. */
export const isDefinitionKey = (key: string) => /(^|_)definition$/i.test(key)

const TWIN = /^(.+)_([a-z]+)_coverage$/i

/**
 * A part the build also wrote over one party's own coverage only — `emerging` and
 * `emerging_td_coverage`, `narrative_clusters` and `narrative_clusters_td_coverage` — named by
 * its base and whose coverage it is. The two are one module with a toggle, never two.
 */
export function coverageTwin(key: string): { base: string; owner: string } | null {
  const match = TWIN.exec(key)
  if (!match) return null
  return { base: match[1]!, owner: ownerLabel(match[2]!) }
}

/** The build's echo chains — what the Echoes module draws — or null when the build writes none. */
export function buildEchoChains(
  sections: Record<string, unknown>,
): { article: SectionArticle; similarity: number | null }[][] | null {
  const echoes = sectionAt(sections, `${ECHO_SECTION_KEY}.echoes`)
  if (!Array.isArray(echoes) || echoes.length === 0) return null
  const display = inferSectionDisplay(echoes)
  if (display.kind !== 'chains') return null
  return display.chains.filter((chain) => chain.length > 1)
}
